import { useState, useCallback } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';

const testimonials = [
  {
    id: 1,
    name: '张伟',
    role: '创业者',
    company: '星辰科技',
    content: '作为一个完全不懂技术的人，我用了不到10分钟就搭建好了公司官网。模板选择多、操作简单，而且免费版功能已经足够用了。强烈推荐！',
    rating: 5,
  },
  {
    id: 2,
    name: '李娜',
    role: '自由摄影师',
    company: 'LN摄影工作室',
    content: '一直想要一个展示作品集的网站，但找开发公司报价太贵。用了这个平台，一下午就完成了自己的摄影网站，客户都说很专业。',
    rating: 5,
  },
  {
    id: 3,
    name: '王芳',
    role: '店主',
    company: '花间集花店',
    content: '开了一家小花店，想在线上展示和接单。用了电商模板，商品展示效果很棒，现在线上订单占了总订单的40%！',
    rating: 5,
  },
  {
    id: 4,
    name: '陈强',
    role: '培训讲师',
    company: '启航教育',
    content: '平台提供的在线教育模板帮我快速建立了培训机构的官网，课程展示和预约功能都很完善。最满意的是SEO优化，搜索排名提升明显。',
    rating: 5,
  },
  {
    id: 5,
    name: '刘洋',
    role: '博主',
    company: '洋哥的旅行笔记',
    content: '作为一个旅行博主，我需要一个好看的博客来记录和分享。这个平台的博客模板设计感很强，写作体验也很流畅。',
    rating: 5,
  },
  {
    id: 6,
    name: '赵敏',
    role: '餐厅老板',
    company: '小城故事餐厅',
    content: '餐饮模板太适合我们了！菜单展示、在线预约、地图导航都一步到位。现在客人可以在网上直接看菜单和预约，方便多了。',
    rating: 5,
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setFormError('留言内容不能超过500个字符');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9slgsl8o1vn02ekuhfg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([k, v]) => [k, v.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  }, []);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '易建站',
    url: 'https://yijianzhan.com',
    description: '免费在线成网站平台，提供零门槛、无需编程的在线建站服务。',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-123-4567',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <main className="pt-16 min-h-screen bg-background-50">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-heading text-foreground-900 mb-6">
              关于易建站
            </h1>
            <p className="text-lg text-foreground-600 leading-relaxed max-w-3xl mx-auto mb-8">
              我们致力于让每个人都能够轻松拥有自己的网站。无论你是个人创作者、中小企业主还是创业者，
              都不需要学习复杂的编程知识，通过我们直观的拖拽式编辑器，就能在几分钟内搭建出专业级的网站。
            </p>
            <p className="text-base text-foreground-500 leading-relaxed max-w-3xl mx-auto">
              自成立以来，我们已经帮助超过<strong>500万</strong>用户成功搭建了他们的网站。
              我们的使命是打破技术壁垒，让建站像写文档一样简单。持续创新、用户至上，是我们不变的追求。
            </p>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full py-14 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl md:text-4xl font-heading text-primary-500 mb-2">500万+</div>
                <div className="text-sm text-foreground-500">服务用户</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-heading text-primary-500 mb-2">2000+</div>
                <div className="text-sm text-foreground-500">精美模板</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-heading text-primary-500 mb-2">8年</div>
                <div className="text-sm text-foreground-500">行业深耕</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-heading text-primary-500 mb-2">99.9%</div>
                <div className="text-sm text-foreground-500">在线率保障</div>
              </div>
            </div>
          </div>
        </section>

        {/* Reputation / Testimonials */}
        <section id="reputation" className="w-full py-16 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-4xl font-heading text-foreground-900 mb-4">
                用户口碑
              </h2>
              <p className="text-base text-foreground-600 max-w-2xl mx-auto">
                听听我们的用户怎么说。每一位用户的成功，都是我们前行的动力。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="p-6 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-5">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-heading text-sm">
                      {t.name.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-400">{t.role} · {t.company}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="w-full py-16 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-4xl font-heading text-foreground-900 mb-4">
                  联系我们
                </h2>
                <p className="text-base text-foreground-600">
                  有任何问题或建议？请随时联系我们，我们会在24小时内回复。
                </p>
              </div>

              <div className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/70">
                <form id="contact-form" data-readdy-form="" onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入咨询主题"
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您想咨询的内容（不超过500字）"
                    ></textarea>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hp-field"
                  />

                  <button
                    type="submit"
                    className="w-full py-3 text-sm font-medium rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    发送消息
                  </button>

                  {formStatus === 'success' && (
                    <p className="mt-4 text-sm text-accent-600 text-center">
                      消息已发送成功，我们会尽快回复您！
                    </p>
                  )}
                  {formStatus === 'error' && (
                    <p className="mt-4 text-sm text-red-500 text-center">
                      {formError || '发送失败，请稍后重试'}
                    </p>
                  )}
                </form>
              </div>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
                <div className="p-5">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <h4 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h4>
                  <p className="text-sm text-foreground-500">support@yijianzhan.com</p>
                </div>
                <div className="p-5">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <h4 className="text-sm font-medium text-foreground-900 mb-1">客服电话</h4>
                  <p className="text-sm text-foreground-500">400-123-4567</p>
                </div>
                <div className="p-5">
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <h4 className="text-sm font-medium text-foreground-900 mb-1">公司地址</h4>
                  <p className="text-sm text-foreground-500">北京市海淀区中关村科技园</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
    </>
  );
}